/**
 * Frontend Translation API Service
 * Uses backend proxy to avoid CORS issues
 */

import SERVER from '../hooks/useServer';

/**
 * Translate text using backend translation endpoint
 * @param {string} text - Text to translate
 * @param {string} targetLang - Target language (en, fr, etc.)
 * @param {string} sourceLang - Source language (default: fr)
 * @returns {Promise<string>} Translated text
 */
export async function translateText(text, targetLang = 'en', sourceLang = 'fr') {
  if (!text || text.trim() === '') {
    return text;
  }

  try {
    const response = await fetch(`${SERVER}/translate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        sourceLang,
        targetLang
      })
    });

    if (!response.ok) {
      throw new Error(`Translation failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.translatedText || text;
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Return original text if translation fails
  }
}

/**
 * Translate multiple texts in batch
 * @param {Array<string>} texts - Array of texts to translate
 * @param {string} targetLang - Target language
 * @param {string} sourceLang - Source language
 * @returns {Promise<Array<string>>} Array of translated texts
 */
export async function translateBatch(texts, targetLang = 'en', sourceLang = 'fr') {
  const translations = await Promise.all(
    texts.map(text => translateText(text, targetLang, sourceLang))
  );
  return translations;
}

/**
 * Translate object fields
 * @param {Object} obj - Object with fields to translate
 * @param {Array<string>} fields - Fields to translate
 * @param {string} targetLang - Target language
 * @param {string} sourceLang - Source language
 * @returns {Promise<Object>} Object with translated fields
 */
export async function translateObject(obj, fields = ['title', 'description'], targetLang = 'en', sourceLang = 'fr') {
  const translated = { ...obj };
  
  for (const field of fields) {
    if (obj[field]) {
      translated[`${field}_${targetLang}`] = await translateText(obj[field], targetLang, sourceLang);
    }
  }
  
  return translated;
}

/**
 * Translate characteristics (main and secondary)
 * @param {Object} characteristics - Characteristics object
 * @param {string} targetLang - Target language
 * @param {string} sourceLang - Source language
 * @returns {Promise<Object>} Translated characteristics
 */
export async function translateCharacteristics(characteristics, targetLang = 'en', sourceLang = 'fr') {
  if (!characteristics || typeof characteristics !== 'object') {
    return characteristics;
  }

  const translated = {};
  
  for (const [key, char] of Object.entries(characteristics)) {
    translated[key] = {
      ...char,
      title_en: await translateText(char.title, targetLang, sourceLang),
      unity_en: char.unity || char.unity_en || '',
      value: char.value // Value stays the same
    };
  }
  
  return translated;
}
